import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const schema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(100),
  email: z.string().trim().email("Enter a valid email").max(200),
  subject: z.string().trim().min(1, "Subject is required").max(200),
  body: z.string().trim().min(1, "Message is required").max(5000),
  website: z.string().optional(), // honeypot
});

type FormValues = z.infer<typeof schema>;

export const ContactDialog = () => {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", subject: "", body: "", website: "" },
  });

  // Pre-fill name + email when signed in
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const user = data?.user;
      if (cancelled || !user) return;
      if (user.email) setValue("email", user.email);
      const meta = user.user_metadata as Record<string, unknown> | undefined;
      const name =
        (meta?.full_name as string) ||
        (meta?.name as string) ||
        (meta?.display_name as string) ||
        "";
      if (name) setValue("name", name);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, setValue]);

  const onSubmit = async (values: FormValues) => {
    try {
      const { error } = await supabase.functions.invoke("contact-submit", {
        body: {
          name: values.name,
          email: values.email,
          subject: values.subject,
          body: values.body,
          website: values.website ?? "",
          pagePath: location.pathname + location.search,
        },
      });
      if (error) throw error;
      setSubmitted(true);
      reset();
    } catch (err) {
      console.error("Contact submit failed", err);
      toast.error("Could not send your message. Please try again in a moment.");
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Reset state on close
      setTimeout(() => {
        setSubmitted(false);
        reset();
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="hover:text-foreground transition-colors"
        >
          Contact
        </button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-lg mkt-contact-dialog"
        overlayClassName="mkt-contact-overlay"
      >
        <DialogHeader>
          <DialogTitle>Contact us</DialogTitle>
          <DialogDescription>
            We read every message and reply within 1 business day.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-foreground">
              Thanks — your message is on its way. We&apos;ll be in touch shortly.
            </p>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Honeypot — visually hidden but in the DOM */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: 1,
                height: 1,
                overflow: "hidden",
              }}
            >
              <label htmlFor="contact-website">Website</label>
              <input
                id="contact-website"
                tabIndex={-1}
                autoComplete="off"
                {...register("website")}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-name" className="sr-only">Name</Label>
              <Input id="contact-name" placeholder="Your name" autoComplete="name" {...register("name")} />
              {errors.name && (
                <p className="text-xs text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-email" className="sr-only">Email</Label>
              <Input
                id="contact-email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-subject" className="sr-only">Subject</Label>
              <Input id="contact-subject" placeholder="Subject" {...register("subject")} />
              {errors.subject && (
                <p className="text-xs text-destructive">{errors.subject.message}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-body" className="sr-only">Message</Label>
              <Textarea
                id="contact-body"
                rows={6}
                placeholder="How can we help?"
                {...register("body")}
              />
              {errors.body && (
                <p className="text-xs text-destructive">{errors.body.message}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send message
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
